import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import Tooltip from 'common/common/Tooltip';
import { TrackEventContext } from 'common/containers/EventContainer';
import { ShowIntercomContext } from 'common/containers/IntercomContainer';
import LandingUserVoiceHelmet from 'common/helmets/LandingUserVoiceHelmet';
import Button from 'common/inputs/Button';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LandingLogos from 'common/LandingLogos';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import UppercaseHeader from 'common/UppercaseHeader';
import withContexts from 'common/util/withContexts';
import AzureDevOpsLogo from 'img/landing/devops-logo.webp';
import GainsightLogo from 'img/landing/gainsight-logo.webp';
import Ida from 'img/landing/ida.webp';
import James from 'img/landing/james.webp';
import MercuryWordmark from 'img/landing/mercury-wordmark.webp';
import MicrosoftTeamsLogo from 'img/landing/microsoft-teams-logo.webp';
import OktaLogo from 'img/landing/okta-logo.webp';
import SegmentLogo from 'img/landing/segment-logo.webp';
import Subdomain1x from 'img/landing/subdomain-1x.webp';
import Subdomain2x from 'img/landing/subdomain-2x.webp';
import UVLogo from 'img/landing/uservoice-logo.webp';
import UVtoCanny from 'img/landing/uv-to-canny.webp';
import Zeb from 'img/landing/zeb-square.webp';

import CannyLogo from 'img/canny-wordmark-white.svg';
import GitHubLogo from 'img/landing/github-logo.svg';
import IntercomLogo from 'img/landing/intercom-logo.svg';
import JiraLogo from 'img/landing/jira-logo.svg';
import SalesforceLogo from 'img/landing/salesforce-logo.svg';
import SlackLogo from 'img/landing/slack-logo.svg';
import ZapierLogo from 'img/landing/zapier-logo.svg';
import ZendeskLogo from 'img/landing/zendesk-logo.svg';

import 'css/components/www/_LandingComparisonPageNew.scss';
import 'css/components/_LandingFooterCTA.scss';

class LandingUserVoice extends Component {
  static propTypes = {
    showIntercom: PropTypes.func,
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed UserVoice Page');
  }

  render() {
    return (
      <div className="landingUserVoice landingComparisonPage">
        <LandingUserVoiceHelmet />
        <div className="mainContentContainer">
          <div className="header">
            <div className="left">
              <h1>Canny is a modern UserVoice&nbsp;alternative</h1>
              <p className="text">
                UserVoice is an expensive, complicated tool, weighed down by features you won’t use
                and a lengthy onboarding&nbsp;process.
              </p>
              <p className="text">
                Canny is a flexible tool that’s both lightweight and powerful. It’s{' '}
                <Link to="/pricing">cost&#8209;effective</Link>, quick to set up, and prioritizes{' '}
                <Link to="/features">features you’ll actually&nbsp;use</Link>.
              </p>
              <Link className="cta" to="/register">
                <Button buttonType="greenButton" value="Get Started" />
              </Link>
            </div>
            <LazyLoadedImage
              className="headerImg"
              loading="eager"
              src={Subdomain1x}
              srcSet={`${Subdomain1x} 1x, ${Subdomain2x} 2x`}
              alt="A feedback board in Canny, a UserVoice alternative, displaying users’ feature requests"
            />
          </div>
          <div className="section">
            <h2 className="sectionHeader">Table of contents</h2>
            <div className="sectionContent">
              <a href="#quick-look" className="text toc-option">
                <p className="number">01</p>
                <p className="option-text">
                  Quick look: Why people prefer Canny over&nbsp;UserVoice
                </p>
              </a>
              <a href="#compared" className="text toc-option">
                <p className="number">02</p>
                <p className="option-text">Canny compared to&nbsp;Uservoice</p>
              </a>
              <a href="#pricing" className="text toc-option">
                <p className="number">03</p>
                <p className="option-text">
                  Canny is a less expensive alternative to&nbsp;UserVoice{' '}
                </p>
              </a>
              <a href="#setting-up" className="text toc-option">
                <p className="number">04</p>
                <p className="option-text">Setting up UserVoice versus setting up&nbsp;Canny</p>
              </a>
              <a href="#integrations" className="text toc-option">
                <p className="number">05</p>
                <p className="option-text">
                  What integrations does UserVoice have? What about&nbsp;Canny?
                </p>
              </a>
              <a href="#customers" className="text toc-option">
                <p className="number">06</p>
                <p className="option-text">Canny customers and use&nbsp;cases</p>
              </a>
              <a href="#migration" className="text toc-option">
                <p className="number">07</p>
                <p className="option-text">Switching to Canny from&nbsp;UserVoice</p>
              </a>
            </div>
          </div>
          <div className="section" id="quick-look">
            <h2 className="sectionHeader">
              Quick look: Why people prefer Canny over&nbsp;UserVoice
            </h2>
            <div className="sectionContent">
              <div className="comparisonChart">
                <div className="comparisonLogoHeader">
                  <div className="canny">
                    <LazyLoadedImage alt="Canny's logo" src={CannyLogo} />
                  </div>
                  <div className="competitorLogo uservoice">
                    <LazyLoadedImage alt="UserVoice's logo" src={UVLogo} />
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <h4 className="title">Only the features you&nbsp;need</h4>
                    <p className="description">
                      We’ll integrate with your existing workflow and allow other tools to do
                      their&nbsp;jobs.
                    </p>
                  </div>
                  <div className="point">
                    <h4 className="title">Feature bloated</h4>
                    <p className="description">
                      Half the battle is figuring out which features you want to turn&nbsp;on.
                    </p>
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <h4 className="title">Simple, straightforward design</h4>
                    <p className="description">
                      Our widgets embed seamlessly into your&nbsp;product.
                    </p>
                  </div>
                  <div className="point">
                    <h4 className="title">Outdated user interface</h4>
                    <p className="description">
                      Their widgets will look out of place in your slick&nbsp;app.
                    </p>
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <h4 className="title">First class support</h4>
                    <p className="description">
                      You won’t need us often but when you do, we’ll be&nbsp;here.
                    </p>
                  </div>
                  <div className="point">
                    <h4 className="title">Poor support</h4>
                    <p className="description">
                      Are you a top tier customer? No? Looks like you’re on your&nbsp;own.
                    </p>
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <h4 className="title">Self-service</h4>
                    <p className="description">
                      We have plans that work for early startups through enterprise.{' '}
                      <Link to="/register">Try&nbsp;it&nbsp;now</Link>.
                    </p>
                  </div>
                  <div className="point">
                    <h4 className="title">Talk to sales</h4>
                    <p className="description">
                      You have to talk to a salesperson to even try their&nbsp;product.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="section" id="compared">
            <h2 className="sectionHeader">Canny compared to&nbsp;UserVoice</h2>
            <div className="sectionContent">
              <p className="text">
                Canny and UserVoice are both solutions that will help you collect and manage product
                feedback better. There’s plenty of overlap in terms of features, and where the two
                tools deliver&nbsp;value.
              </p>
              <p className="text">That said, there are also big&nbsp;differences.</p>
              <p className="text">
                UserVoice is one of the oldest user feedback tools on the market. It’s jam-packed
                with features, a lot of which aren’t actually that useful when it comes to making
                informed decisions about your product. It’s a tool built for enterprises with
                special use cases—which most businesses don’t&nbsp;have.
              </p>
              <div className="quoteContainer">
                <p className="quote">
                  "We were looking for an affordable customer feedback solution with a clean
                  interface, simple usability, and the feature set we needed. Canny came out on top
                  over&nbsp;UserVoice."
                </p>
                <div className="credit">
                  <LazyLoadedImage src={James} alt="James McCubbin" />
                  <p className="text">
                    <div className="name">James McCubbin</div>
                    <h4 className="title founderTitle">
                      Co-founder at{' '}
                      <a href="https://campus.app/" rel="noopener" target="_blank">
                        Campus
                      </a>
                    </h4>
                  </p>
                </div>
              </div>
              <p className="text">
                Their onboarding process is time-consuming and complicated, too—you’ll need to speak
                with a sales representative multiple times just to even try the&nbsp;product.
              </p>
              <p className="text">
                At Canny, we’re all about delivering features you actually need and providing an
                easy and hassle-free&nbsp;experience.
              </p>
              <p className="text">
                You can try our product instantly, without talking to anyone on our team (unless you
                want&nbsp;to).
              </p>
            </div>
          </div>
          <div className="section" id="pricing">
            <h2 className="sectionHeader">
              Canny is a less expensive alternative to&nbsp;UserVoice
            </h2>
            <div className="sectionContent">
              <p className="text">
                There’s no way around it: UserVoice is an expensive tool. Their lowest pricing
                option clocks in at around $15K a&nbsp;year.
              </p>
              <p className="text">
                But, it’s not just the cost—determining how much you’ll actually pay for UserVoice
                isn’t a straightforward&nbsp;process.
              </p>
              <p className="text">
                You won’t be able to get a UserVoice price quote without multiple calls. They don’t
                offer a self-serve option, which isn’t&nbsp;convenient.
              </p>
              <p className="text">
                You'll also have to pay more when you hit their tracked user limits. Your costs
                aren't predictable and can increase&nbsp;suddenly.
              </p>
              <p className="text">
                Canny offers transparent pricing and self-serve options —– so you can test out the
                tool and start collecting feedback quickly. We even offer a completely free plan to
                get you&nbsp;started.
              </p>
              <div className="pricingContainer">
                <div className="pricingPlans">
                  <div className="pricingPlan">
                    <p className="planName">Free</p>
                    <div className="price">
                      <p className="number">$0</p>
                      <p className="perMonth">/ mo</p>
                    </div>
                  </div>
                  <div className="pricingPlan">
                    <p className="planName">Core, starts at</p>
                    <div className="price">
                      <p className="number">$19</p>
                      <p className="perMonth">/ mo</p>
                    </div>
                  </div>
                  <div className="pricingPlan">
                    <p className="planName">Pro, starts at</p>
                    <div className="price">
                      <p className="number">$79</p>
                      <p className="perMonth">/ mo</p>
                    </div>
                  </div>
                </div>
                <div className="morePricing">
                  <p className="morePricingText">
                    More details and custom pricing options&nbsp;available
                  </p>
                  <Link className="cta" to="/pricing">
                    View&nbsp;pricing
                  </Link>
                </div>
              </div>

              <p className="text">
                Need a demo, or pricing that’s tailored to your specific needs? You got it. We’re
                standing by—you’ll be able to{' '}
                <Link to="/request-demo">hop on a call with our team</Link> and discuss your
                options.
              </p>
            </div>
          </div>
          <div className="section" id="setting-up">
            <h2 className="sectionHeader">Setting up UserVoice vs. setting up&nbsp;Canny</h2>
            <div className="sectionContent">
              <p className="text">When it comes to setup, UserVoice is a&nbsp;hassle.</p>
              <p className="text">
                To try UserVoice, you’ll have to have a call with their team. You can’t try the
                product without talking to someone&nbsp;first.
              </p>
              <p className="text">
                Since UserVoice is a complex tool with a lot of features, it takes a while to set up
                and customize. You’ll need help from a salesperson to get it set up for
                you—self-serve onboarding isn’t really an&nbsp;option.
              </p>
              <div className="quoteContainer">
                <p className="quote">
                  "Getting Canny set up couldn’t have been easier. We started with a blank canvas
                  and allowed our users to paint the&nbsp;rest."
                </p>
                <div className="credit">
                  <LazyLoadedImage src={Zeb} alt="Zeb Evans" />
                  <p className="text">
                    <div className="name">Zeb Evans</div>
                    <h4 className="title founderTitle">
                      Founder, CEO at{' '}
                      <a href="https://clickup.com/" rel="noopener" target="_blank">
                        ClickUp
                      </a>
                    </h4>
                  </p>
                </div>
              </div>
              <p className="text">
                Installing Canny is a breeze and usually takes less than 15&nbsp;minutes!
              </p>
              <p className="text">
                You can use Canny on our Free plan until you're ready to unlock more value. You can
                reference our extensive knowledge base and get everything up and running yourself.
                Our team is also always here to&nbsp;assist.
              </p>
            </div>
          </div>
          <div className="section" id="integrations">
            <h2 className="sectionHeader">
              What integrations does UserVoice have? What about&nbsp;Canny?
            </h2>
            <div className="sectionContent">
              <p className="text">
                Both Canny and UserVoice integrate with the tools you already use. But, Canny
                integrates with more tools than UserVoice—and we’re regularly adding
                new&nbsp;integrations.{' '}
              </p>
              <p className="text">Here’s how the two stack&nbsp;up:</p>
              <div className="integrationsComparison">
                <div className="comparisonLogoHeader connected">
                  <div className="canny">
                    <LazyLoadedImage src={CannyLogo} alt="Canny logo" />
                  </div>
                  <div className="competitorLogo uservoice">
                    <LazyLoadedImage src={UVLogo} alt="UserVoice logo" />
                  </div>
                </div>
                <div className="integrationsGrids">
                  <div className="integrationsGrid">
                    <div className="integrationsGridInner">
                      <Link to="/integrations/slack" className="integration slackImage">
                        <Tooltip position="top" value="Slack">
                          <LazyLoadedImage src={SlackLogo} alt="Canny's Slack integration" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/jira" className="integration jiraImage">
                        <Tooltip position="top" value="Jira">
                          <LazyLoadedImage src={JiraLogo} alt="Canny's Jira integration" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/zendesk" className="integration zendeskImage">
                        <Tooltip position="top" value="Zendesk">
                          <LazyLoadedImage src={ZendeskLogo} alt="Canny's Zendesk integration" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/intercom" className="integration intercomImage">
                        <Tooltip position="top" value="Intercom">
                          <LazyLoadedImage src={IntercomLogo} alt="Canny's Intercom integration" />
                        </Tooltip>
                      </Link>
                      <a
                        href="https://zapier.com/apps/canny/integrations"
                        rel="noopener"
                        className="integration zapierImage">
                        <Tooltip position="top" value="Zapier">
                          <LazyLoadedImage src={ZapierLogo} alt="Canny's Zapier integration" />
                        </Tooltip>
                      </a>
                      <Link to="/integrations/salesforce" className="integration salesforceImage">
                        <Tooltip position="top" value="Salesforce">
                          <LazyLoadedImage
                            src={SalesforceLogo}
                            alt="Canny's Salesforce integration"
                          />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/github" className="integration githubImage">
                        <Tooltip position="top" value="GitHub">
                          <LazyLoadedImage src={GitHubLogo} alt="Canny's GitHub integration" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/segment" className="integration segmentImage">
                        <Tooltip position="top" value="Segment">
                          <LazyLoadedImage src={SegmentLogo} alt="Canny's Segment integration" />
                        </Tooltip>
                      </Link>
                      <a
                        href="https://help.canny.io/en/articles/4029303-okta-sso-integration"
                        className="integration oktaImage">
                        <Tooltip position="bottom" value="Okta">
                          <LazyLoadedImage src={OktaLogo} alt="Canny's Okta integration" />
                        </Tooltip>
                      </a>
                      <a
                        href="https://help.canny.io/en/articles/4343053-microsoft-teams-integration"
                        className="integration microsoftTeamsImage">
                        <Tooltip position="top" value="Microsoft Teams">
                          <LazyLoadedImage
                            src={MicrosoftTeamsLogo}
                            alt="Canny's Microsoft Teams integration"
                          />
                        </Tooltip>
                      </a>
                    </div>
                  </div>
                  <div className="integrationsGrid">
                    <div className="integrationsGridInner">
                      <div className="integration">
                        <Tooltip position="top" value="Slack">
                          <LazyLoadedImage src={SlackLogo} alt="Slack logo" />
                        </Tooltip>
                      </div>
                      <div className="integration">
                        <Tooltip position="top" value="Jira">
                          <LazyLoadedImage src={JiraLogo} alt="Jira logo" />
                        </Tooltip>
                      </div>
                      <div className="integration">
                        <Tooltip position="top" value="Zendesk">
                          <LazyLoadedImage src={ZendeskLogo} alt="Zendesk logo" />
                        </Tooltip>
                      </div>
                      <div className="integration">
                        <Tooltip position="top" value="Azure DevOps">
                          <LazyLoadedImage src={AzureDevOpsLogo} alt="Azure DevOps logo" />
                        </Tooltip>
                      </div>
                      <div className="integration">
                        <Tooltip position="top" value="Gainsight">
                          <LazyLoadedImage src={GainsightLogo} alt="Gainsight logo" />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text">
                You can{' '}
                <Link to="/integrations">learn more about Canny’s integrations&nbsp;here</Link>.
              </p>
            </div>
          </div>
          <div className="section" id="customers">
            <h2 className="sectionHeader">Canny customers and use&nbsp;cases</h2>
            <div className="sectionContent">
              <p className="text">
                UserVoice is a solution for very big businesses, and they’ve got the roster to prove
                it. They’re not a good fit for smaller businesses and&nbsp;startups.
              </p>
              <p className="text">
                Because Canny is a flexible, customizable, and reliable solution, we have customers
                all the way from 2-person startups to billion dollar companies like{' '}
                <Link to="/case-studies">CircleCI&nbsp;and&nbsp;Mercury</Link>.
              </p>
              <Link className="customerItemContents mercury" to="/case-studies/mercury">
                <p className="text">
                  <div className="top">
                    <div className="wordmark">
                      <LazyLoadedImage src={MercuryWordmark} alt="Mercury logo" />
                    </div>
                    <p className="description">
                      How Mercury found a drastic improvement in efficiency and&nbsp;productivity
                    </p>
                  </div>
                  <UppercaseHeader>
                    Read their story <span className="icon-chevron-right" />
                  </UppercaseHeader>
                </p>
                <LazyLoadedImage className="profile" src={Ida} alt="Ida Ström" />
              </Link>
              <p className="text">
                And, Canny isn’t just for collecting feedback from customers.{' '}
                <Link to="/use-cases/internal-feedback">Internal teams</Link> also use it to{' '}
                <Link to="/features/product-roadmap">
                  link user feedback to their product roadmap
                </Link>{' '}
                and <Link to="/features/product-changelog">announce feature&nbsp;updates</Link>.
              </p>
            </div>
          </div>
          <div className="section importData" id="migration">
            <h2 className="sectionHeader">Switching to Canny from&nbsp;UserVoice</h2>
            <div className="sectionContent">
              <p className="text">
                Currently a UserVoice customer? Make the switch—it’s easy and&nbsp;free.
              </p>
              <LazyLoadedImage
                src={UVtoCanny}
                className="UVtoCanny"
                alt="Switch to Canny, the UserVoice alternative, with free data migration"
              />
              <p className="text">
                We can migrate all of your data from UserVoice to Canny within 48 hours. Reach out
                to us to get&nbsp;started.{' '}
              </p>
            </div>
          </div>
          <LandingLogos />
          <LandingFooterCTA style="solid" />
        </div>
      </div>
    );
  }
}

export default compose(
  withContexts({
    showIntercom: ShowIntercomContext,
    trackEvent: TrackEventContext,
  }),
  hot,
)(LandingUserVoice);
